import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterMedia from "../components/RegisterMedia";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Utils from "../utils/utils";

type DataIndex = keyof Types.MediaDto;

function MediaList() {
    const alert = useAlert();

    const [medias, setMedias] = useState<Array<Types.MediaDto>>();
    const [currentMedia, setcurrentMedia] = useState<Types.MediaDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteMedia, setOpenDeleteMedia] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.MediaDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.MediaDto> = [
        {
            align: "center",
            title: String.mediaId,
            dataIndex: "mediaId",
            key: "mediaId",
            ...getColumnSearchProps("mediaId"),
        },
        {
            align: "center",
            title: String.mediaTitle,
            dataIndex: "mediaTitle",
            key: "mediaTitle",
            ...getColumnSearchProps("mediaTitle"),
        },
        {
            align: "center",
            title: String.mediaUrl,
            dataIndex: "mediaUrl",
            key: "mediaUrl",
            ...getColumnSearchProps("mediaUrl"),
        },
    ];

    const requestAxiosGetMedias = async () => {
        const response = await utilAxiosWithAuth().get(Request.MEDIA_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetMedias,
        error: errorGetMedias,
        data: resultGetMedias,
        execute: requestGetMedias,
    } = useAsyncAxios(requestAxiosGetMedias);

    useEffect(() => {
        if (resultGetMedias === null) return;

        setMedias(resultGetMedias.medias);
    }, [resultGetMedias]);

    useEffect(() => {
        if (errorGetMedias === null) return;

        alert.setAlert(errorGetMedias.response.data.code, "Error", errorGetMedias.response.data.message);
    }, [errorGetMedias]);

    const requestAxiosRemoveMedia = async (media: Types.MediaDto) => {
        const response = await utilAxiosWithAuth().delete(Request.MEDIA_URL + "/" + media?.mediaId);
        return response.data;
    };

    const {
        loading: loadingRemoveMedia,
        error: errorRemoveMedia,
        data: resultRemoveMedia,
        execute: requestRemoveMedias,
    } = useAsyncAxios(requestAxiosRemoveMedia);

    useEffect(() => {
        if (resultRemoveMedia === null) return;

        alert.setAlert(AlertType.SUCCESS, "옵션 삭제 성공", "옵션 삭제를 완료하였습니다.");

        requestGetMedias();
    }, [resultRemoveMedia]);

    useEffect(() => {
        if (errorRemoveMedia === null) return;

        alert.setAlert(errorRemoveMedia.response.data.code, "Error", errorRemoveMedia.response.data.message);
    }, [errorGetMedias]);

    useEffect(() => {
        requestGetMedias();
    }, []);

    const onRow = (value: Types.MediaDto) => {
        setEditMode("none");
        setcurrentMedia(value);
    };

    const handleDeleteMedia = () => {
        if (currentMedia !== undefined) setOpenDeleteMedia(true);
    };

    const handleDeleteMediaOk = () => {
        setOpenDeleteMedia(false);
        requestRemoveMedias(currentMedia);
    };

    const handleDeleteMediaCancel = () => {
        setOpenDeleteMedia(false);
    };

    const onRegisterMedia = () => {
        requestGetMedias();
        setEditMode("none");
    };

    return (
        <>
            <Col span={6}>
                <Divider orientation="left">
                    <span>미디어</span>
                </Divider>
            </Col>
            <DataTable hasScroll={false} columns={columns} rowKey="mediaId" dataSource={medias} onRow={onRow} />
            <Row>
                <Col span={24} style={{ padding: "10px 0", textAlign: "right" }}>
                    <Space>
                        <Button
                            onClick={() => {
                                editMode === "add" ? setEditMode("none") : setEditMode("add");
                            }}
                        >
                            {String.userAdd}
                        </Button>
                        <Button
                            onClick={() => {
                                editMode === "update" ? setEditMode("none") : setEditMode("update");
                            }}
                        >
                            {String.userUpdate}
                        </Button>
                        <Button onClick={() => handleDeleteMedia()} danger>
                            {String.userRemove}
                        </Button>
                    </Space>
                </Col>
            </Row>

            {editMode === "add" && <RegisterMedia editMode={editMode} onRegisterMedia={onRegisterMedia} />}

            {editMode !== "add" && currentMedia !== undefined && (
                <RegisterMedia editMode={editMode} media={currentMedia} onRegisterMedia={onRegisterMedia} />
            )}

            {openDeleteMedia && (
                <Modal open={openDeleteMedia} onOk={handleDeleteMediaOk} onCancel={handleDeleteMediaCancel}>
                    {currentMedia?.mediaTitle} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default MediaList;
